import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="reporting-v2-config"
// Handles checkbox cascade behavior and select-all for reporting v2 config
export default class extends Controller {
  static targets = ["syncHeader", "attributionHeader", "reportingHeader", "sync", "attribution", "reporting"]

  connect() {
    this.updateSyncHeader()
    this.updateAttributionHeader()
    this.updateReportingHeader()
  }

  // When sync is unchecked, auto-uncheck attribution and reporting for that row
  syncChanged(event) {
    const checkbox = event.target
    if (!checkbox.checked) {
      const rowId = checkbox.dataset.rowId
      this.#findRowCheckboxes("attribution", rowId).forEach(cb => { cb.checked = false })
      this.#findRowCheckboxes("reporting", rowId).forEach(cb => { cb.checked = false })
      this.updateAttributionHeader()
      this.updateReportingHeader()
    }
  }

  // When attribution or reporting is checked, auto-check sync for that row
  displayChanged(event) {
    const checkbox = event.target
    if (checkbox.checked) {
      const rowId = checkbox.dataset.rowId
      this.#findRowCheckboxes("sync", rowId).forEach(cb => { cb.checked = true })
      this.updateSyncHeader()
    }
  }

  // Column-level select all/deselect all
  toggleSync() {
    const checked = this.syncHeaderTarget.checked
    this.syncTargets.forEach(checkbox => {
      if (!checkbox.disabled) checkbox.checked = checked
    })
    // If unchecking sync, also uncheck attribution and reporting
    if (!checked) {
      this.attributionTargets.forEach(checkbox => {
        if (!checkbox.disabled) checkbox.checked = false
      })
      this.reportingTargets.forEach(checkbox => {
        if (!checkbox.disabled) checkbox.checked = false
      })
      this.updateAttributionHeader()
      this.updateReportingHeader()
    }
  }

  toggleAttribution() {
    const checked = this.attributionHeaderTarget.checked
    this.attributionTargets.forEach(checkbox => {
      if (!checkbox.disabled) checkbox.checked = checked
    })
    // If checking attribution, also check sync
    if (checked) {
      this.syncTargets.forEach(checkbox => {
        if (!checkbox.disabled) checkbox.checked = true
      })
      this.updateSyncHeader()
    }
  }

  toggleReporting() {
    const checked = this.reportingHeaderTarget.checked
    this.reportingTargets.forEach(checkbox => {
      if (!checkbox.disabled) checkbox.checked = checked
    })
    // If checking reporting, also check sync
    if (checked) {
      this.syncTargets.forEach(checkbox => {
        if (!checkbox.disabled) checkbox.checked = true
      })
      this.updateSyncHeader()
    }
  }

  updateSyncHeader() {
    this.#updateHeaderState(this.syncHeaderTarget, this.syncTargets)
  }

  updateAttributionHeader() {
    this.#updateHeaderState(this.attributionHeaderTarget, this.attributionTargets)
  }

  updateReportingHeader() {
    this.#updateHeaderState(this.reportingHeaderTarget, this.reportingTargets)
  }

  // Private

  #updateHeaderState(header, targets) {
    if (!header) return

    const enabledCheckboxes = targets.filter(cb => !cb.disabled)
    if (enabledCheckboxes.length === 0) return

    const allChecked = enabledCheckboxes.every(cb => cb.checked)
    const someChecked = enabledCheckboxes.some(cb => cb.checked)

    header.checked = allChecked
    header.indeterminate = !allChecked && someChecked
  }

  #findRowCheckboxes(targetName, rowId) {
    const targets = this[`${targetName}Targets`]
    return targets.filter(cb => cb.dataset.rowId === rowId)
  }
}
